import i18n from 'src/config/i18n';
import { Http } from 'src/shared/services/Http';

import { ProgramEnum } from '../../models/programs';
import {
    FetchedSurveyQuestion,
    FetchSeveralProgramSurveysData,
    FetchSurveyData,
    GenericSurveyAnswer,
    NormalizedSeveralSurveysData,
    NormalizedSurveyData,
    SubmitProgramSurveyData,
    SubmitSeveralProgramSurveysData,
} from '../models/surveys';
import { GenericProgramSurvey } from '../models/surveysGeneric';

export class SurveysService {
    constructor(private http: Http) {}

    public fetchProgramSurveyQuestions = (
        programId: ProgramEnum,
        surveyId: GenericProgramSurvey,
    ): Promise<FetchedSurveyQuestion[]> =>
        this.http.GET<FetchedSurveyQuestion[]>(
            `programs/${programId}/surveys/${surveyId}/questions/`,
        );

    public fetchProgramSurveyAnswers = (
        programId: ProgramEnum,
        surveyId: GenericProgramSurvey,
    ): Promise<GenericSurveyAnswer[]> =>
        this.http.GET<GenericSurveyAnswer[]>(
            `programs/${programId}/surveys/${surveyId}/answers/latest/`,
        );

    public async fetchProgramSurvey(data: FetchSurveyData): Promise<NormalizedSurveyData> {
        const { programId, surveyId } = data;

        const fetchedQuestions = await this.fetchProgramSurveyQuestions(programId, surveyId);
        const fetchedAnswers = await this.fetchProgramSurveyAnswers(programId, surveyId);

        return {
            programId,
            surveyId,
            questions:
                fetchedQuestions !== undefined
                    ? fetchedQuestions.map(question => ({
                          id: question.id,
                          ...(question.data as any),
                      }))
                    : [],
            answers:
                fetchedQuestions !== undefined
                    ? fetchedQuestions.map(question => {
                          const answer = fetchedAnswers.find(
                              fetchedAnswer => fetchedAnswer.question === question.id,
                          );

                          return {
                              question: question.id,
                              type: question.data.type,
                              data: answer ? answer.data : {},
                              renderId: question.data.renderId,
                              notRequired: question.data.notRequired,
                          } as GenericSurveyAnswer;
                      })
                    : [],
        };
    }

    public async fetchSeveralProgramSurveys(
        data: FetchSeveralProgramSurveysData,
    ): Promise<NormalizedSeveralSurveysData> {
        const { programId, surveysIds } = data;

        const fetchedSeveralSurveys = surveysIds.map(async surveyId =>
            this.fetchProgramSurvey({ programId, surveyId }),
        );

        const programSurveys = await Promise.all(fetchedSeveralSurveys).then(surveys =>
            surveys.reduce(
                (acc, survey) => ({
                    ...acc,
                    [survey.surveyId]: {
                        questions: survey.questions,
                        answers: survey.answers,
                    },
                }),
                {},
            ),
        );

        return { programId, programSurveys };
    }

    public submitProgramSurvey(data: SubmitProgramSurveyData): Promise<GenericSurveyAnswer[]> {
        const { programId, surveyId, answers } = data;

        if (!answers) {
            return Promise.reject(i18n.t('notifications.noSurveyAnswers'));
        }

        return this.http.POST<GenericSurveyAnswer[]>(
            `programs/${programId}/surveys/${surveyId}/answers/`,
            answers,
        );
    }

    public async submitSeveralProgramSurveys(
        data: SubmitSeveralProgramSurveysData,
    ): Promise<GenericSurveyAnswer[]> {
        const { programId, answers } = data;

        const submittedSeveralSurveys = Object.keys(answers).map(async surveyId =>
            this.submitProgramSurvey({
                programId,
                surveyId: surveyId as GenericProgramSurvey, // Needed because of string in AnswersBySurveysIds Record declaration
                answers: answers[surveyId],
            }),
        );

        const requestedData = Promise.all(submittedSeveralSurveys).then(value =>
            value.reduce((acc, survey) => ({
                ...acc,
                ...survey,
            })),
        );

        return requestedData;
    }
}
